import { gql } from "lib/apolloClient";
import { attachmentFragment, userNameFragment } from "modules/CommonFragments";

const assessmentFragment = gql`
  fragment assessmentItem on Assessment {
    id
    templateId
    assessmentTitle: title {
      id
      value
    }
    assessmentType {
      id
      value
    }
    image {
      id
      value
    }
    academicTerm {
      id
      lockingStatus
    }
    lockingInfo {
      isLocked
    }
    isIncludedInCourseFlow
    unitPlan {
      id
      allCollaborators(filters: $collaboratorFilters) {
        edges {
          permission
          node {
            id
          }
        }
      }
    }
  }
`;

const learningCourseFragment = gql`
  fragment learningCourseFlowItem on LearningCourseFlow {
    id
    label
    displaySequence
    parent {
      id
    }
    learningCourse {
      id
    }
    depth
    itemType
    state
    isEditAllowed
    isSharingAllowed
    sharedDetails {
      class {
        id
      }
      students {
        id
      }
    }
    lockingInfo {
      isLocked
    }
    item {
      ... on Attachment {
        ...attachmentItem
      }
      ... on Assessment {
        ...assessmentItem
      }
    }
  }
  ${assessmentFragment}
  ${attachmentFragment.attachment}
`;

export const classPlannerFragment = {
  classPlannerItemFragment: gql`
    fragment classPlannerItem on WeeklyPlannerTemplate {
      id
      title
      startDate
      endDate
      createdAt
      enabledSections {
        sections
      }
      createdBy {
        ...userNameFields
      }
      enabledSections {
        sections
      }
      updatedAt
      updatedBy {
        ...userNameFields
      }
      courses {
        id
        title
        primaryGrade {
          id
        }
      }
      defaultAccessState
      collaborators {
        edges {
          permission
          node {
            id
          }
        }
      }
      exceptionalCollaborators(filters: $exceptionalCollaboratorFilters) {
        edges {
          node {
            id
            courses {
              id
            }
          }
          permission
        }
      }
      isSharedWithFamilies
      isSharedWithStudents
      __typename
    }
    ${userNameFragment}
  `,
  weeklyPlannerSectionFragment: gql`
    fragment weeklyPlannerSectionItem on WeeklyPlannerSection {
      id
      title
      description
      startDate
      endDate
      startTime
      endTime
      isRecurringEvent
      recurranceStartDate
      recurranceEndDate
      recurrenceSetting {
        frequency
        weekDays
        interval
        endDate
        monthDays
        setPositions
        timezone
        count
      }
      items {
        id
        itemType
        isItemDragged
        sectionItem: item {
          ... on Attachment {
            ...attachmentItem
          }
          ... on Assessment {
            ...assessmentItem
          }
          ... on Assignment {
            id
            contentType
            content {
              __typename
              ... on Assessment {
                ...assessmentItem
                learningCourse {
                  id
                  permissions {
                    isManageAssignmentsAndResourcesInCourseFlow
                  }
                }
              }
            }
          }
          ... on LearningCourseFlow {
            ...learningCourseFlowItem
          }
        }
      }
      colorCode
      __typename
    }
    ${attachmentFragment.attachment}
    ${assessmentFragment}
    ${learningCourseFragment}
  `,
  weeklyPlannerSectionItemsFragment: gql`
    fragment weeklyPlannerSectionItem on WeeklyPlannerSection {
      id
      items {
        id
        itemType
        isItemDragged
        sectionItem: item {
          ... on Attachment {
            ...attachmentItem
          }
          ... on Assessment {
            ...assessmentItem
          }
          ... on Assignment {
            id
            contentType
            content {
              __typename
              ... on Assessment {
                ...assessmentItem
                learningCourse {
                  id
                  permissions {
                    isManageAssignmentsAndResourcesInCourseFlow
                  }
                }
              }
            }
          }
          ... on LearningCourseFlow {
            ...learningCourseFlowItem
          }
        }
      }
      __typename
    }
    ${attachmentFragment.attachment}
    ${assessmentFragment}
    ${learningCourseFragment}
  `,
  calendarEvent: gql`
    fragment calendarEvent on CalendarEvent {
      id
      start: startDate
      end: endDate
      title
      grades
      tag {
        id
        colorCode
      }
    }
  `,
};
